import React from 'react';
import {ServiceBase} from '../ServiceBase';
import {
    IModalDialogHandler,
    IDialogService,
    ShowModalOptions, ISimpleTextModalOptions
} from './dialog.service.interface';
import {ModalDialogComponent} from '../../components/modal-dialog/modal-dialog.component';
import {Check} from '@sparkware/app-utils';
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import { action, observable, runInAction, makeObservable } from 'mobx';
import {CGP_OVERLAY_OPEN} from "../../themes/css-global-classes";

import {namedSetTimeout} from "../../../common/utils/time-functions";
import {
    SimpleMessageDialogComponent
} from "../../components/modal-dialog/standard-dialogs/simple-message/simple-message-dialog.component";
import {
    MODAL_DIALOG_ANIMATION_DURATION
} from "../../components/modal-dialog/boxes/overlay-content/dialog-overlay-constants";

const DIALOG_CONTENT = '.cy-modal-dialog-content';
const ANALYTICS_CATEGORY = 'Dialog_Service';

interface IOpenDialog {
    dialogHandler: ModalDialogHandler;
    dialogComponent: React.ReactNode;
}

export class DialogService extends ServiceBase implements IDialogService {

    constructor(services:IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        this._setCloseAllDialogsHook();
        this.services.deepLink.then(deepLink => {
            this.reaction(() =>  deepLink.lastTimeDeepLinkWasExecuted,
                ()=> this.closeAllDialogs()
            );
        })

        this.reaction(() => this.hasOpenDialogs, (hasOpenDialogs) => {
            if (hasOpenDialogs) {
                this._disableScroll();
            } else {
                this._enableScroll();
            }
        })
    }

    private _setCloseAllDialogsHook(): void {
        this.services.sdk.events.onGameOpen(this.closeAllDialogs.bind(this));

        this.reaction(() => [
                this.services.user.isAuthorized,
                this.services.routing.currentRoute
            ],
        ()=> this.closeAllDialogs()
        );
    }

    @observable.ref
    private _currentOpenModalDialogs: IOpenDialog[] = [];

    @action.bound
    private _setCurrentOpenModalDialogs(newDialogs:  IOpenDialog[]): void {
        this._currentOpenModalDialogs = newDialogs;
    }

    public get currentDialogs(): React.ReactNode[] {
        return  this._currentOpenModalDialogs.map(dialog => dialog.dialogComponent);
    }

    private _onDialogCloseHook =  (closedHandler: ModalDialogHandler) => {

        this._setCurrentOpenModalDialogs(this._currentOpenModalDialogs.filter(dialog => dialog.dialogHandler.zIndex !== closedHandler.zIndex));
        this.services.gameRunner.then(gameRunner => {
            gameRunner.currentRunningGame?.gameCommunicationChannel.resumeGamePlay();
        })
    };

    private _enableScroll() {
        this.services.dom.body.classList.remove(CGP_OVERLAY_OPEN);
    }

    private _disableScroll() {
        this.services.dom.body.classList.add(CGP_OVERLAY_OPEN);
    }

    private _getNextZIndex(isHighPriority?: boolean, forceZOrder?:number): number {

        if(forceZOrder) {
            return forceZOrder;
        }

        const zIndexes = this._currentOpenModalDialogs.map(dialog => dialog.dialogHandler.zIndex);

        if(zIndexes.length === 0) {
            return isHighPriority ? this.services.sdk.getCGPMaxOverlayZIndex + 1 : 200;
        }

        if(isHighPriority) {
            return Math.max(...zIndexes, this.services.sdk.getCGPMaxOverlayZIndex) + 1;
        } else {
            return Math.max(...zIndexes) + 1;
        }
    }

    get hasOpenDialogs(): boolean {
        return !Check.isEmpty(this._currentOpenModalDialogs.filter(d => !d.dialogHandler.isHidden));
    }

    countDialogs(opts: {includeHidden: boolean;}): number {
        if (opts.includeHidden) {
            return this._currentOpenModalDialogs.length;
        }
        return this._currentOpenModalDialogs.filter(d => !d.dialogHandler.isHidden).length;
    }

    closeAllDialogs() {
        const dialogsToClose = this._currentOpenModalDialogs.filter(dialog => !dialog.dialogHandler.shouldBeClosedOnlyByUser)
        dialogsToClose.forEach((dialog)=> {
            dialog.dialogHandler.destroy();
        });

        this._setCurrentOpenModalDialogs(this._currentOpenModalDialogs.filter(dialog => dialog.dialogHandler.shouldBeClosedOnlyByUser));
    }

    showModal(renderContent: (dialogHandler: IModalDialogHandler) => React.ReactNode, options?: ShowModalOptions): IModalDialogHandler {
        const modalZIndex = this._getNextZIndex(options?.isHighPriority,options?.forceZOrder);

        this.services.gameRunner.then(gameRunner => {
            if(modalZIndex > gameRunner.gameFrameZIndex) {
                this.services.analytics.sendOrbitUserFlowEvent(ANALYTICS_CATEGORY, 'stopCurrentGameAutoplay', `modalZIndex: ${modalZIndex}`);
                gameRunner.currentRunningGame?.gameCommunicationChannel.stopAutoplay();
            }
        })


        options = {
            hideCloseButton: false,
            hideBorders: false,
            showOpenAndCloseAnimation: false,
            ...options
        };

        const dialogHandler = new ModalDialogHandler(modalZIndex, this.services, this._onDialogCloseHook, options);

        const isHybrid = this.services.device.hybrid.isHybrid;

        if(isHybrid && options?.showPopupAboveSecondWebViewHybrid){
            // check this option.
            this.services.sdk.openPopUpOnSecondWebView(DIALOG_CONTENT, 1000);
        }

        const dialogComponent = (<ModalDialogComponent key={dialogHandler.zIndex}
                                                       services={this.services}
                                                       dialogHandler={dialogHandler}
                                                       renderContent={renderContent}
                                                       dialogOptions={options}/>);

        this._setCurrentOpenModalDialogs([
            ...this._currentOpenModalDialogs,
            {
                dialogHandler: dialogHandler,
                dialogComponent: dialogComponent
            }
        ]);

        return dialogHandler;
    }

    getMaxCGPOverlayZIndex(): number {
        return this.services.sdk.getCGPMaxOverlayZIndex;
    }

    showSimpleTextModal(options: ISimpleTextModalOptions): IModalDialogHandler {
        return this.showModal(dialogHandler => (<SimpleMessageDialogComponent dialogHandler={dialogHandler} text={options.text} buttons={options.buttons || []} icon={options.icon}/>), {
            title: options.title || (options.showEmptyTitle ? '  ' : ''),
            icon: options.iconSvg,
            mobileCenterHeader: true,
            hideCloseButton: options.hideCloseButton,
            isHighPriority: true,
            disableFullscreenMobile: Check.isNullOrUndefined(options.disableFullscreenMobile) ? true : options.disableFullscreenMobile,
            forceCentralization: true,
            showPopupAboveSecondWebViewHybrid: Boolean(options.showPopupAboveSecondWebViewHybrid),
            onClosedByXButton: options.onClosedByXButton,
            onClosed: options.onClosed,
            shouldBeClosedOnlyByUser: options.shouldBeClosedOnlyByUser
        });
    }
}


class ModalDialogHandler implements IModalDialogHandler {
    constructor(zIndex: number,
                private _services,
                private _onCloseHook: (handler: ModalDialogHandler) => void,
                private _options: ShowModalOptions) {
        makeObservable(this);

        this.isHidden = Boolean(this._options.createAsHidden);
        this._zIndex = zIndex;
    }


    private _isClosed = false;

    public get shouldBeClosedOnlyByUser(): boolean {
        return this._options.shouldBeClosedOnlyByUser;
    }

    @observable.ref
    private _zIndex: number;

    get zIndex(): number {
        return this._zIndex;
    }

    set zIndex(value: number) {
        runInAction(() => {
            this._zIndex = value;
        });
    }

    @observable.ref
    isHidden: boolean = false;

    display(): void {
        runInAction(() => {
            this.isHidden = false;
        });

        if(this._options.onDisplay) {
            this._options.onDisplay();
        }
    }

    hide(): void {
        runInAction(() => {
            this.isHidden = true;
        });

        if(this._options.onHide) {
            this._options.onHide();
        }
    }

    @observable.ref
    public shouldApplyClosingAnimation: boolean = false;

    @action.bound
    close(isClosedWithXButton: boolean = false): void {
        if(this._isClosed) {
            return;
        }


        this.shouldApplyClosingAnimation = this._options.showOpenAndCloseAnimation;
        if(this.shouldApplyClosingAnimation) {
            namedSetTimeout('DIALOG_CLOSE_ANIMATION_TIMER', ()=> this._applyCallbackAndCloseDialog(isClosedWithXButton), 0.9 * MODAL_DIALOG_ANIMATION_DURATION);
        } else {
            this._applyCallbackAndCloseDialog(isClosedWithXButton)
        }

    }

    destroy(): void {
        this._applyCallbackAndCloseDialog(true);
    }

    private _applyCallbackAndCloseDialog = (isClosedWithXButton: boolean): void => {
        this._isClosed = true;

        if(isClosedWithXButton && this._options.onClosedByXButton) {
            try {
                this._options.onClosedByXButton();
            } catch (err) {
                this._services.logger.error(`onClosedByXButton dialog handler failed! Debug info: ${this._options?.debugInfo} | Dialog title: ${this._options?.title}`, this._options.onClosedByXButton, err);
            }
        }

        if(this._options.onClosed) {
            try {
                this._options.onClosed();
            } catch (err) {
                this._services.logger.error(`onClosed dialog handler failed! Debug info: ${this._options?.debugInfo} | Dialog title: ${this._options?.title}`, this._options.onClosed, err);
            }
        }

        this._onCloseHook(this);

        if(this._options?.showPopupAboveSecondWebViewHybrid){
            this._services.sdk.closePopUpOnSecondWebView();
        }

    }
}